/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { render, screen, fireEvent, waitFor } from '@testing-library/preact';
import { expect } from 'chai';
import { PieChart } from '../';
import { ComponentProps } from 'preact/compat';
import { MenuItem } from '#UNSAFE_Menu';
import { matchTranslationBundle } from '#utils/UNSAFE_matchTranslationBundle';
import { RootEnvironment, RootEnvironmentProvider } from '#UNSAFE_Environment';
import { queryAllBy } from '#utils/PRIVATE_visTestUtils/domUtil';
import {
  getTestContextMenuForNoData,
  getTestContextMenuViaKeyboard,
  getTestContextMenuViaMouse
} from '#utils/PRIVATE_visTestUtils/contextMenuUtils';
import { setupUseTextDimensionsDependenciesMock } from '#/../test/mocks/mockUseTextDimensionsDependencies';
const pieData = [
  { id: 1, value: 5 },
  { id: 2, value: 4 },
  { id: 3, value: 4 }
];

type PieDataItem = (typeof pieData)[0];

type actionListenerType = {
  clicked: boolean;
  id?: number;
  ids?: number[];
};
type PieChartProps = ComponentProps<typeof PieChart<PieDataItem['id'], PieDataItem>>;

describe('Test PieChart component', () => {
  beforeEach(() => {
    setupUseTextDimensionsDependenciesMock(window);
  });

  it('render', async function () {
    render(
      <PieChart data={pieData} width={'400px'} height={'400px'} aria-label="pie chart label" />
    );
    const ariaText = screen.getAllByLabelText('pie chart label');
    expect(ariaText).not.null;

    const ariaRole = screen.getByRole('application');
    expect(ariaRole).not.null;
  });
  if (typeof jest === 'undefined') {
    it('should render nothing when all data values are zero', async function () {
      const zeroData = [
        { id: 1, value: 0 },
        { id: 2, value: 0 }
      ];
      await setupPieChart({ data: zeroData });
      const chart = screen.getByRole('application');
      const pieSlices: HTMLElement[] = queryAllBy('data-oj-private-object')(chart, 'pieSlice');
      expect(pieSlices.length).to.equal(0);
    });

    it('should render nothing when all data values are negative', async function () {
      const negativeData = [
        { id: 1, value: -5 },
        { id: 2, value: -10 }
      ];
      await setupPieChart({ data: negativeData });
      const chart = screen.getByRole('application');
      const pieSlices: HTMLElement[] = queryAllBy('data-oj-private-object')(chart, 'pieSlice');
      expect(pieSlices.length).to.equal(0);
    });

    it('should render only slices for positive values', async function () {
      const mixedData = [
        { id: 1, value: 0 },
        { id: 2, value: -3 },
        { id: 3, value: 7 }
      ];
      await setupPieChart({ data: mixedData });
      const chart = screen.getByRole('application');
      const pieSlices: HTMLElement[] = queryAllBy('data-oj-private-object')(chart, 'pieSlice');
      expect(pieSlices.length).to.equal(1);
      expect(pieSlices[0].getAttribute('data-oj-private-item-index')).to.equal('0');
    });

    it('trigger context menu on mouse right click with correct context', async () => {
      const config: ComponentProps<typeof PieChart>['contextMenuConfig'] = {
        // The menu items returned by itemsRenderer must be valid children for the Menu component
        itemsRenderer: () => {
          return (
            <>
              <MenuItem label="MenuItem1"></MenuItem>
              <MenuItem label="MenuItem2"></MenuItem>
              <MenuItem label="MenuItem3"></MenuItem>
            </>
          );
        }
      };
      await setupPieChart({ contextMenuConfig: config });
      const pieChart = screen.getByRole('application');
      expect(pieChart).not.null;
      getTestContextMenuViaMouse(pieChart);
    });

    it('trigger context menu from Keyboard (shift + f10) with correct context', async () => {
      let type: string | undefined, menuContext;

      const config: ComponentProps<typeof PieChart>['contextMenuConfig'] = {
        // The menu items returned by itemsRenderer must be valid children for the Menu component
        itemsRenderer: (context) => {
          type = context.type;
          menuContext = context;
          return (
            <>
              <MenuItem label="MenuItem1"></MenuItem>
              <MenuItem label="MenuItem2"></MenuItem>
              <MenuItem label="MenuItem3"></MenuItem>
            </>
          );
        }
      };

      await setupPieChart({ contextMenuConfig: config });
      const pieChart = screen.getByRole('application');
      getTestContextMenuViaKeyboard(pieChart, menuContext, type);
    });

    it('should not trigger context menu when there is no data ', async () => {
      const config: ComponentProps<typeof PieChart>['contextMenuConfig'] = {
        // The menu items returned by itemsRenderer must be valid children for the Menu component
        itemsRenderer: () => {
          return (
            <>
              <MenuItem label={`MenuItem1`}></MenuItem>
              <MenuItem label={`MenuItem2`}></MenuItem>
              <MenuItem label={`MenuItem2`}></MenuItem>
            </>
          );
        }
      };

      await setupPieChart({ contextMenuConfig: config, data: [] });
      const pieChart = screen.getByRole('application');
      getTestContextMenuForNoData(pieChart);
    });
    it('drilling with selection none', async function () {
      const actionListenerObj: actionListenerType = { clicked: false, id: undefined };
      const onItemDrill: PieChartProps['onItemDrill'] = (detail) => {
        actionListenerObj.clicked = true;
        actionListenerObj.id = detail.id;
      };
      const { container } = await setupPieChart({ onItemDrill, drilling: 'on' });
      const root = container as HTMLElement;
      const pieSlices = queryAllBy('data-oj-private-item-index');
      const firstItem = pieSlices(root, '0')[0];

      // expect(activeElement?.getAttribute('data-oj-private-item-index')).to.equal(item);
      fireEvent.pointerUp(firstItem);
      await waitFor(() => expect(actionListenerObj.clicked).equals(true));
      await waitFor(() => expect(actionListenerObj.id).equals(1));
    });
  }
});

const setupPieChart = async (options?: Partial<PieChartProps>) => {
  const defaultProps = {
    data: pieData,
    width: '400px' as const,
    height: '400px' as const,
    ariaLabel: 'pie chart label'
  };
  const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
  const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
  const env: Partial<RootEnvironment> = {
    translations: { '@oracle/oraclejet-preact': translations }
  };
  const pieChartProps = { ...defaultProps, ...options };

  return render(
    <RootEnvironmentProvider environment={env}>
      <PieChart {...pieChartProps} />
    </RootEnvironmentProvider>
  );
};
